import DOMPurify from 'dompurify';

/**
 * Configuration pour la sanitization HTML
 * Permet uniquement les éléments et attributs sûrs
 */
const ALLOWED_TAGS = [
  'p', 'br', 'span', 'div',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'strong', 'b', 'em', 'i', 'u', 's', 'strike',
  'ul', 'ol', 'li',
  'a',
  'blockquote', 'pre', 'code',
  'img',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'hr',
];

const ALLOWED_ATTR = [
  'href', 'target', 'rel',
  'src', 'alt', 'width', 'height',
  'class', 'id',
  'style',
];

/**
 * Sanitize HTML content to prevent XSS attacks
 * Uses DOMPurify with strict configuration
 */
export function sanitizeHtml(dirty: string): string {
  if (!dirty) return '';

  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    ADD_ATTR: ['target'],
    // Force all links to open in new tab with noopener
    FORCE_BODY: true,
  });
}

/**
 * Sanitize HTML and add security attributes to links
 */
export function sanitizeHtmlWithSecureLinks(dirty: string): string {
  const clean = sanitizeHtml(dirty);

  // Add rel="noopener noreferrer" to all links with target="_blank"
  return clean.replace(
    /<a([^>]*?)target="_blank"([^>]*?)>/gi,
    '<a$1target="_blank" rel="noopener noreferrer"$2>'
  );
}

/**
 * Strip all HTML tags, keeping only text content
 */
export function stripHtml(dirty: string): string {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, { ALLOWED_TAGS: [] });
}

/**
 * Check if a string contains potentially dangerous HTML
 */
export function containsDangerousHtml(html: string): boolean {
  if (!html) return false;

  const dangerous = [
    /<script/i,
    /javascript:/i,
    /on\w+\s*=/i, // onclick, onerror, etc.
    /<iframe/i,
    /<object/i,
    /<embed/i,
    /<form/i,
    /data:/i,
  ];

  return dangerous.some(pattern => pattern.test(html));
}
